/**
 * UpsideMLDetail — Single ticker upside opportunity detail page
 *
 * Professional flat design matching UpsideMLDashboard.
 * Shows probability history, position sizing, prediction table.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  ShowChart as ChartIcon,
  ExpandMore as ExpandMoreIcon,
  Bolt as BoltIcon,
  Speed as SpeedIcon,
  CalendarMonth as CalendarIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { upsideMLAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS — Matching UpsideMLDashboard palette
   ═══════════════════════════════════════════════════════════════ */

const TIER = {
  3: { label: 'HOT', bg: '#16a34a', text: '#15803d', light: '#f0fdf4', gradient: 'linear-gradient(135deg, #16a34a, #059669)' },
  2: { label: 'WARM', bg: '#ea580c', text: '#c2410c', light: '#fff7ed', gradient: 'linear-gradient(135deg, #ea580c, #f97316)' },
  1: { label: 'MILD', bg: '#2563eb', text: '#1d4ed8', light: '#eff6ff', gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)' },
  0: { label: 'COOL', bg: '#9ca3af', text: '#6b7280', light: '#f9fafb', gradient: 'linear-gradient(135deg, #9ca3af, #d1d5db)' },
};

/* ── Collapsible Section ────────────────────────────────── */
const Section = ({ title, icon, children, defaultOpen = true, tooltip, contentSx }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, cursor: 'pointer',
          background: open ? 'linear-gradient(135deg, #fafafa 0%, #fff 100%)' : '#fafafa',
          borderBottom: open ? '1px solid #f3f4f6' : 'none',
          transition: 'all 0.2s',
          '&:hover': { background: '#f9fafb' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && <Box sx={{ color: '#9ca3af', display: 'flex' }}>{icon}</Box>}
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <InfoIcon sx={{ fontSize: 14, color: '#d1d5db', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: '#f3f4f6', transition: 'transform 0.3s',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          <ExpandMoreIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
        </Box>
      </Box>
      <Collapse in={open} timeout={300}>
        <Box sx={{ p: 2.5, ...contentSx }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const UpsideMLDetail = () => {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await upsideMLAPI.getStockDetail(ticker, days);
      setHistory(res.data || []);
    } catch (err) {
      setError(err.message || t('upsideML.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [ticker, days, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Computed ────────────────────────────────────────── */
  const latest = history.length > 0 ? history[0] : null;
  const latestTier = latest?.opportunity_tier || 0;
  const tierStyle = TIER[latestTier] || TIER[0];

  const stats = useMemo(() => {
    if (!history.length) return {};
    const probs = history.map(h => h.ml_prob);
    const oppDays = history.filter(h => h.opportunity_signal).length;
    return {
      avgProb: (probs.reduce((a, b) => a + b, 0) / probs.length * 100).toFixed(1),
      maxProb: (Math.max(...probs) * 100).toFixed(1),
      minProb: (Math.min(...probs) * 100).toFixed(1),
      oppDays,
      oppPct: (oppDays / history.length * 100).toFixed(0),
    };
  }, [history]);

  const trendDir = useMemo(() => {
    if (history.length < 3) return null;
    const recent3 = history.slice(0, 3).map(h => h.ml_prob);
    const avg3 = recent3.reduce((a, b) => a + b, 0) / 3;
    const older3 = history.slice(3, 6).map(h => h.ml_prob);
    if (older3.length === 0) return null;
    const avgOlder = older3.reduce((a, b) => a + b, 0) / older3.length;
    return avg3 > avgOlder + 0.03 ? 'rising' : avg3 < avgOlder - 0.03 ? 'falling' : null;
  }, [history]);

  // Confidence from ensemble_std
  const confidence = useMemo(() => {
    if (!latest?.ensemble_std) return null;
    const std = latest.ensemble_std;
    if (std < 0.05) return { level: 'high', color: '#16a34a', bg: '#f0fdf4' };
    if (std < 0.10) return { level: 'med', color: '#ea580c', bg: '#fff7ed' };
    return { level: 'low', color: '#dc2626', bg: '#fef2f2' };
  }, [latest]);

  const chartData = useMemo(() => {
    return [...history].reverse().map(h => ({
      date: h.date.slice(5),
      upside: +(h.ml_prob * 100).toFixed(1),
      smoothed: h.smoothed_prob ? +(h.smoothed_prob * 100).toFixed(1) : null,
      threshold: h.dynamic_threshold ? +(h.dynamic_threshold * 100).toFixed(1) : null,
      position: +(h.target_position * 100).toFixed(0),
    }));
  }, [history]);

  /* ─── Render ──────────────────────────────────────────── */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
        <CircularProgress size={40} thickness={5} sx={{ color: '#16a34a' }} />
        <Typography sx={{ fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>
          {t('upsideML.failedToLoad', `Đang tải ${ticker}...`)}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}
          action={<Chip label={t('upsideML.retry')} onClick={fetchData} size="small"
            sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 600 }} />}
        >{error}</Alert>
      </Box>
    );
  }

  if (!latest) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Alert severity="info" sx={{ borderRadius: 1.5 }}>{t('upsideML.noData')}</Alert>
      </Box>
    );
  }

  const currentProb = (latest.ml_prob * 100).toFixed(1);
  const currentPos = (latest.target_position * 100).toFixed(0);
  const currentThreshold = latest.dynamic_threshold ? (latest.dynamic_threshold * 100).toFixed(0) : null;

  let actionLabel, actionColor;
  if (latest.buy_signal) { actionLabel = t('upsideML.actions.strongBuy'); actionColor = '#16a34a'; }
  else if (latestTier >= 3) { actionLabel = t('upsideML.actions.buy'); actionColor = '#22c55e'; }
  else if (latestTier >= 2) { actionLabel = t('upsideML.actions.accumulate'); actionColor = '#ea580c'; }
  else if (latestTier >= 1) { actionLabel = t('upsideML.actions.watch'); actionColor = '#2563eb'; }
  else { actionLabel = t('upsideML.actions.skip'); actionColor = '#9ca3af'; }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={t('upsideML.detail.back')} arrow>
            <IconButton onClick={() => navigate('/upside-dashboard')} size="small"
              sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
              <BackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: tierStyle.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 14px ${tierStyle.bg}4D`,
          }}>
            <TrendingUpIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {ticker?.toUpperCase()}
              </Typography>
              <Chip label={tierStyle.label} size="small" sx={{
                height: 20, fontSize: 10, fontWeight: 800, bgcolor: tierStyle.bg, color: '#fff',
              }} />
              {trendDir && (
                <Chip
                  label={trendDir === 'rising' ? t('upsideML.detail.trend.rising') : t('upsideML.detail.trend.falling')}
                  size="small" variant="outlined"
                  sx={{
                    height: 20, fontSize: 10, fontWeight: 700,
                    borderColor: trendDir === 'rising' ? '#16a34a' : '#dc2626',
                    color: trendDir === 'rising' ? '#16a34a' : '#dc2626',
                  }}
                />
              )}
              {confidence && (
                <Tooltip title={t('upsideML.confidenceTooltip', { level: t(`upsideML.confidenceLevels.${confidence.level}`) })} arrow>
                  <Chip label={t(`upsideML.confidenceLevels.${confidence.level}`)} size="small" sx={{
                    height: 20, fontSize: 9.5, fontWeight: 700,
                    bgcolor: `${confidence.color}14`, color: confidence.color,
                  }} />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('upsideML.detail.title', { ticker: ticker?.toUpperCase() })}
              {latest.date && ` · ${latest.date}`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={days} exclusive
            onChange={(_, v) => v && setDays(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600, textTransform: 'none' } }}
          >
            {[14, 30, 60].map(d => (
              <ToggleButton key={d} value={d}>{d}d</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton onClick={fetchData} size="small" sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ═══════════════════════════════════════════════════════
            HERO METRICS
            ═══════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{
          borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden',
          background: `linear-gradient(135deg, ${tierStyle.light} 0%, #fff 60%)`,
        }}>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap',
            '& > *': { flex: '1 1 0', minWidth: 140, px: 2.5, py: 2, borderRight: '1px solid #f3f4f6' },
            '& > *:last-child': { borderRight: 'none' },
          }}>
            {/* Upside Probability */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <TrendingUpIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('upsideML.detail.currentUpside')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: tierStyle.bg, lineHeight: 1 }}>
                  {currentProb}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: tierStyle.bg }}>%</Typography>
              </Box>
              {currentThreshold && (
                <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                  {t('upsideML.detail.threshold')}: {currentThreshold}%
                </Typography>
              )}
            </Box>

            {/* Position Size */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <SpeedIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('upsideML.detail.positionSize')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {currentPos}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>%</Typography>
              </Box>
              <Box sx={{ mt: 0.8, width: 80, height: 4, borderRadius: 2, bgcolor: '#f3f4f6', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', borderRadius: 2, width: `${Math.min(currentPos, 100)}%`, background: tierStyle.gradient, transition: 'width 0.4s' }} />
              </Box>
            </Box>

            {/* Action */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <BoltIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('upsideML.detail.tableSignal')}
                </Typography>
              </Box>
              <Chip label={actionLabel} size="small" sx={{
                height: 26, fontSize: 12, fontWeight: 800,
                bgcolor: actionColor, color: '#fff',
                mt: 0.5,
              }} />
            </Box>

            {/* Opportunity Days */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <CalendarIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('upsideML.detail.oppDays', { d: days })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {stats.oppDays || 0}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>
                  / {history.length}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                {stats.oppPct}%
              </Typography>
            </Box>

            {/* Average */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <BarChartIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('upsideML.detail.avgUpside', { d: days })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {stats.avgProb}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>%</Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                {t('upsideML.detail.avgUpsideSub', { max: stats.maxProb })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* ═══════════════════════════════════════════════════════
            CHARTS — Side by side
            ═══════════════════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 2 }}>
          <Section
            title={t('upsideML.detail.upsideProbOverTime')}
            icon={<ChartIcon sx={{ fontSize: 16 }} />}
            tooltip={t('upsideML.detail.tooltipUpside')}
          >
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="upsideGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                <YAxis domain={[0, 100]} fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <RTooltip
                  contentStyle={{
                    borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    background: '#fff',
                  }}
                  formatter={(val, name) => {
                    const label = name === 'upside' ? t('upsideML.detail.tooltipUpside')
                      : name === 'smoothed' ? t('upsideML.detail.tooltipSmoothed')
                        : t('upsideML.detail.tooltipThreshold');
                    return [`${val}%`, label];
                  }}
                />
                <ReferenceLine y={50} stroke="#16a34a" strokeDasharray="4 4" strokeOpacity={0.4} />
                <Area
                  dataKey="upside" name={t('upsideML.detail.upsidePct')}
                  fill="url(#upsideGrad)" stroke="#16a34a" strokeWidth={2}
                />
                <Line
                  dataKey="smoothed" name={t('upsideML.detail.smoothed')}
                  stroke="#2563eb" strokeWidth={1.5} dot={false} strokeDasharray="4 4"
                />
                <Line
                  dataKey="threshold" name={t('upsideML.detail.threshold')}
                  stroke="#ea580c" strokeWidth={1} dot={false} strokeDasharray="6 3"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Section>

          <Section
            title={t('upsideML.detail.recommendedPosition')}
            icon={<SpeedIcon sx={{ fontSize: 16 }} />}
          >
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="posGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                <YAxis domain={[0, 100]} fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <RTooltip
                  contentStyle={{
                    borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    background: '#fff',
                  }}
                  formatter={(val) => [`${val}%`, t('upsideML.detail.tooltipPosition')]}
                />
                <ReferenceLine y={50} stroke="#2563eb" strokeDasharray="4 4" strokeOpacity={0.4} />
                <Area
                  dataKey="position" name={t('upsideML.detail.positionPct')}
                  fill="url(#posGrad)" stroke="#2563eb" strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Section>
        </Box>

        {/* ═══════════════════════════════════════════════════════
            PREDICTION HISTORY TABLE
            ═══════════════════════════════════════════════════════ */}
        <Section
          title={t('upsideML.detail.predictionHistory')}
          icon={<CalendarIcon sx={{ fontSize: 16 }} />}
          contentSx={{ p: 0 }}
        >
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)',
                  '& th': {
                    fontWeight: 700, fontSize: 11, color: '#15803d', py: 1,
                    borderBottom: '2px solid #bbf7d0', whiteSpace: 'nowrap',
                    bgcolor: '#f0fdf4',
                  },
                }}>
                  <TableCell>{t('upsideML.detail.tableDate')}</TableCell>
                  <TableCell align="right">{t('upsideML.detail.tableUpside')}</TableCell>
                  <TableCell align="center">{t('upsideML.detail.tableTier')}</TableCell>
                  <TableCell align="right">{t('upsideML.detail.tablePosition')}</TableCell>
                  <TableCell align="right">{t('upsideML.detail.tableThreshold')}</TableCell>
                  <TableCell align="center">{t('upsideML.detail.tableSignal')}</TableCell>
                  <TableCell align="center">{t('upsideML.detail.tableBuy')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row, idx) => {
                  const tier = row.opportunity_tier || 0;
                  const style = TIER[tier] || TIER[0];
                  const prob = (row.ml_prob * 100).toFixed(1);
                  const pos = (row.target_position * 100).toFixed(0);
                  const th = row.dynamic_threshold ? (row.dynamic_threshold * 100).toFixed(0) : '—';
                  const isLatest = idx === 0;

                  return (
                    <TableRow
                      key={row.date}
                      sx={{
                        background: isLatest
                          ? `linear-gradient(90deg, ${style.light} 0%, #fff 40%)`
                          : 'transparent',
                        '& td': { borderBottom: '1px solid #f3f4f6', py: 1 },
                        '& td:first-of-type': { borderLeft: `3px solid ${style.bg}` },
                        '&:hover': { bgcolor: '#f9fafb' },
                        transition: 'background 0.15s',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontWeight: isLatest ? 700 : 600, fontSize: 12, color: isLatest ? '#1e293b' : '#6b7280' }}>
                            {row.date}
                          </Typography>
                          {isLatest && (
                            <Chip label={t('upsideML.detail.today', 'Mới nhất')} size="small" sx={{
                              height: 16, fontSize: 9, fontWeight: 700,
                              bgcolor: '#16a34a', color: '#fff',
                              '& .MuiChip-label': { px: 0.5 },
                            }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, justifyContent: 'flex-end' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, color: style.bg }}>
                            {prob}%
                          </Typography>
                          <Box sx={{ width: 40, height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: '#f3f4f6' }}>
                            <Box sx={{
                              height: '100%', borderRadius: 2,
                              width: `${Math.min(row.ml_prob * 100, 100)}%`,
                              bgcolor: style.bg,
                            }} />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={style.label} size="small" sx={{
                          height: 18, fontSize: 10, fontWeight: 800,
                          bgcolor: style.bg, color: '#fff',
                          '& .MuiChip-label': { px: 0.6 },
                        }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{pos}%</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>{th}%</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {row.opportunity_signal ? (
                          <Chip label={t('upsideML.signals.opportunity')} size="small" sx={{
                            height: 18, fontSize: 9, fontWeight: 700,
                            bgcolor: '#dcfce7', color: '#16a34a',
                            '& .MuiChip-label': { px: 0.5 },
                          }} />
                        ) : (
                          <Typography sx={{ fontSize: 11, color: '#d1d5db' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.buy_signal ? (
                          <Chip
                            icon={<BoltIcon sx={{ fontSize: 11 }} />}
                            label={t('upsideML.signals.strongBuy')}
                            size="small"
                            sx={{
                              height: 18, fontSize: 9, fontWeight: 700,
                              bgcolor: '#16a34a', color: '#fff',
                              '& .MuiChip-icon': { color: 'inherit' },
                              '& .MuiChip-label': { px: 0.5 },
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 11, color: '#d1d5db' }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      </Box>
    </Box>
  );
};

export default UpsideMLDetail;
